import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { Subscription } from 'rxjs';

import {
    ApiError,
    AuthApiService,
    FundMandatoryProductPriceTypesInterface, FundProductInterface, FundProductPriceModel,
    FundProductPricesService, FundProductTypesEnum,
    FundProductWithPricesModel,
    HttpStatusCodesEnum,
    OperatorInterface,
    OperatorsService, ProductCategory,
    ServerResponse
} from '@spa/api';

import { FormComponent } from 'appShared/widgets';
import { FormInterface } from 'appShared/interfaces';
import { DialogActionsEnum, DialogComponent, DialogConfigInterface } from '../../../../shared/components/dialog';

import { AddEditProductPriceFormErrorStatesEnum } from 'appEntries/panel/enums';
import { PanelSettingsProductsAddEditPriceForm } from 'appEntries/panel/forms';

import { AddEditProductPriceMessages } from 'appAssets/messages';
import { AlertTypesEnum } from 'appShared/enums';
import { AlertsService } from 'appShared/services';

@Component({
    selector: 'app-add-edit-product-price',
    templateUrl: './add-edit-product-price.component.html',
    styleUrls: ['./add-edit-product-price.component.scss']
})
export class AddEditProductPriceComponent extends DialogComponent<AddEditProductPriceComponent>
    implements FormInterface, OnInit, OnDestroy {

    private updateProductPricesSubscription: Subscription;
    private operatorSubscription: Subscription;
    private operatorName: string;

    public form: PanelSettingsProductsAddEditPriceForm;

    constructor(
        private readonly fundProductPricesService: FundProductPricesService,
        private readonly authApiService: AuthApiService,
        public dialogRef: MatDialogRef<AddEditProductPriceComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {
            product: FundProductInterface,
            prices: FundProductPriceModel[]
            isAddNew: boolean,
            updatingDate: string | null,
            config: DialogConfigInterface,
            mandatoryTypes: FundMandatoryProductPriceTypesInterface,
        },
    ) {
        super(dialogRef);

        this.config = data.config;
    }

    private handleRequest(request: ServerResponse<FundProductPriceModel[]>): void {
        this.updateProductPricesSubscription = request.subscribe(
            this.dialogSubscribeHandler.bind(
                this,
                (data: FundProductPriceModel[]) => {
                    this.data.prices = data;

                    this.config.process.succeedMessage = this.data.isAddNew
                        ? AddEditProductPriceMessages.AddPriceSucceed.message
                        : AddEditProductPriceMessages.EditPriceSucceed.message;
                },
                (errors: ApiError[]) => {
                    this.config.process.failedMessage = errors
                        .map((error: ApiError) => {
                            switch (error.status) {
                                case AddEditProductPriceFormErrorStatesEnum.RegisterProductWithPricesFailed:
                                    return AddEditProductPriceMessages.AddPriceFailed.message;
                                case AddEditProductPriceFormErrorStatesEnum.RegisterProductWithPricesFailedDuplicate:
                                    return AddEditProductPriceMessages.AddPriceFailedDuplicate.message;
                                case AddEditProductPriceFormErrorStatesEnum.UpdateProductWithPricesFailed:
                                    return AddEditProductPriceMessages.EditPriceFailed.message;
                                default:
                                    return AddEditProductPriceMessages.UnknownError.message;
                            }
                        })
                        .join('<br>');
                }
            )
        );
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.initForm();

        this.operatorName = this.authApiService.getAuthenticatedUser().email;
    }

    ngOnDestroy(): void {
        if (this.updateProductPricesSubscription) {
            this.updateProductPricesSubscription.unsubscribe();
        }

        if (this.operatorSubscription) {
            this.operatorSubscription.unsubscribe();
        }
    }

    getFormMessages(controlLabel?: string): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel);
    }

    initForm(): void {
        this.form = new PanelSettingsProductsAddEditPriceForm(
            this.data.product,
            this.data.prices,
            this.fundProductPricesService,
        );

        this.form.initForm(this.data.mandatoryTypes, this.data.updatingDate);
    }

    validateForm(): boolean {
        return this.form.validateForm(this.getFormMessages);
    }

    onFormSubmit(action?: number): void {
        if (action === DialogActionsEnum.ProcessFinished) {
            return this.dialogRef.close(this.data.prices);
        }

        if (action === DialogActionsEnum.Decline) {
            return this.dialogRef.close(false);
        }

        if (this.validateForm()) {
            this.startLoading(() => {
                const request = this.form.createRequest(
                    this.data.product.id,
                    this.operatorName,
                    this.data.isAddNew);

                if (request) {
                    this.handleRequest(request);
                } else {
                    this.dialogRef.close(this.data.prices);
                }
            });
        }
    }
}
